<template>
  <main>
    <Loader
      :loader="formLoader"
      label="Carregando..."
      loaderType="progress"
      loaderSize="h-96"
    />

    <section v-if="!formLoader" class="bg-h-primary-50 border py-2">
      <div class="container mx-auto">
        <BreadcrumbComponent :home="home" :items="items" />
      </div>
    </section>

    <section
      v-if="!formLoader"
      class="container mx-auto flex w-12/12 sm:w-10/12 lg:w-8/12 justify-center my-10"
    >
      <div class="w-full">
        <form @submit.prevent="submitQuestion">
          <div class="flex justify-between items-center mb-10">
            <p class="text-xl md:text-2xl font-bold">
              {{
                $route.params.id
                  ? $t("quiz.teacher.edit")
                  : $t("quiz.teacher.add")
              }}
            </p>
            <div class="flex items-center gap-2">
              <Button
                id="btn-cancel"
                :label="$t('labels.cancel')"
                class="btn-outline p-button-sm"
                @click="confirmModal()"
              />
              <Button
                id="btn-submit-questions"
                :label="$t('labels.save')"
                class="btn-primary p-button-sm"
                type="submit"
              />
            </div>
          </div>

          <div class="flex flex-col gap-10">
            <div class="flex flex-col gap-5">
              <div class="flex flex-col gap-3">
                <p class="font-bold text-lg">
                  {{ $t("question.prompt.title") }}
                </p>
                <div class="p-5 bg-white rounded-md shadow-sm border">
                  <div id="prompt">
                    <p class="mb-2">{{ $t("question.prompt.title") }}</p>
                    <Textarea
                      id="description-input"
                      :autoResize="true"
                      rows="5"
                      cols="30"
                      :placeholder="$t('question.prompt.empty')"
                      v-model="form.prompt"
                      :class="v$.form.prompt.$error ? 'p-invalid' : ''"
                      :maxlength="999"
                    />
                    <Errors
                      :errors="v$.form.prompt.$errors"
                      :actualLength="form.prompt?.length"
                      :maxLength="999"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div class="flex flex-row items-center gap-5">
                  <p class="font-bold text-lg col-span-12 my-5">Itens</p>
                  <p v-if="selectCorrect" class="text-sm text-h-error-400">
                    {{
                      $t("labels.generic", {
                        pt: "Você deve indicar qual o item correto",
                        en: "You must indicate which item is correct",
                      })
                    }}
                  </p>
                </div>
                <div
                  id="item-question"
                  class="grid grid-cols-12 gap-3"
                  v-for="(item, i) in form.items"
                  :key="i"
                >
                  <div
                    class="p-5 bg-white rounded-md shadow-sm border col-span-10 mb-5"
                  >
                    <div id="enunciated-item" class="mb-1">
                      <Textarea
                        id="question-input"
                        :autoResize="true"
                        rows="3"
                        cols="30"
                        :placeholder="$t('question.prompt.items.empty')"
                        v-model="item.answerText"
                        :class="
                          v$.form.items.$each.$response.$errors[i].answerText
                            ?.length !== 0 && v$.form.$error
                            ? 'p-invalid'
                            : ''
                        "
                        :maxlength="999"
                      />
                      <Errors
                        :errors="
                          v$.form.items.$each.$response.$errors[i].answerText
                        "
                        :actualLength="item.answerText?.length"
                        :maxLength="999"
                      >
                        <template #msg>
                          {{ v$.form.$error ? $t("validations.required") : "" }}
                        </template>
                      </Errors>
                    </div>
                  </div>

                  <div
                    id="item-action"
                    class="col-span-2 flex flex-col justify-center items-center"
                  >
                    <v-icon
                      name="hi-solid-check-circle"
                      :class="`${
                        item.id === form.correctItem?.id || item.correct
                          ? 'text-h-success-400'
                          : 'text-gray-400'
                      } cursor-pointer hover:scale-95 transition-all ${
                        v$.form.items.$each.$response.$errors[i].correct
                          ?.length !== 0 && v$.form.$error
                          ? 'border-2 border-red-500 rounded-full'
                          : ''
                      }`"
                      :scale="
                        item.id === form.correctItem?.id ? 4 : 3 || item.correct
                      "
                      @click="chooseCorrectItem(item, form.items)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
    <ConfirmDialog />
  </main>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { Chapter } from "@/models/chapter.model";
import {
  ItemQuestion,
  MaterialReferences,
  Question,
} from "@/models/quiz.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { GenericsUtils } from "@/utils/generics.util";
import {
  getItemLocalStorage,
  removeItemLocalStorage,
} from "@/utils/save-storage.util";
import useVuelidate from "@vuelidate/core";
import { helpers, required as req } from "@vuelidate/validators";
import { defineComponent } from "vue";
import { ObjectiveService } from "../../objective.service";
import { QuizServiceTeacher } from "../quiz.service";

export default defineComponent({
  name: "formQuestion",
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    service(): QuizServiceTeacher {
      return new QuizServiceTeacher();
    },
    serviceObjective(): ObjectiveService {
      return new ObjectiveService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    course(): any {
      return JSON.parse(getItemLocalStorage("objective"));
    },
  },
  data() {
    return {
      quizId: this.$route.params.quizId as string,
      objectiveId: this.$route.params.objectiveId as string,
      questionId: this.$route.params.id as string,
      form: new Question(),
      formLoader: false as boolean,
      autocomplete: {
        suggestions: [],
        selected: null,
      } as any,
      home: {
        label: "tradicional",
        command: () => this.$router.go(-1),
      },
      items: [
        {
          label: this.$route.params?.id
            ? this.$t("labels.generic", {
                pt: "Editar questão",
                en: "Edit question",
              })
            : this.$t("labels.generic", {
                pt: "Adicionar questão",
                en: "Add question",
              }),
        },
      ],
      selectCorrect: false as boolean,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        prompt: { required },
        items: {
          $each: helpers.forEach({
            answerText: { req },
            correct: { req },
          }),
        },
      },
    };
  },
  mounted() {
    this.form.items = new Array(4).fill(null).map(() => new ItemQuestion());
    this.getQuestionById();
  },
  watch: {
    $route() {
      removeItemLocalStorage("objective");
    },
  },
  methods: {
    confirmModal(): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Os dados não salvos serão perdidos! Tem certeza que desejar sair?`,
          en: `Unsaved data will be lost! Are you sure you want to quit?`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da tela",
          en: "Exit Screen",
        }),
        acceptLabel: this.$t("labels.generic", {
          pt: "Sair",
          en: "Exit",
        }),
        rejectLabel: this.$t("labels.generic", {
          pt: "Não",
          en: "No",
        }),
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.$router.go(-1);
        },
      });
    },
    mountItemTOS(data: Question): void {
      data.itemTOS = this.form.items?.map((e) => {
        e.id = e.id ?? undefined;
        e.answerType = "TEXT";
        return e;
      });
    },
    submitQuestion(): void {
      this.v$.form.$touch();
      if (!this.v$.form.$error) {
        this.formLoader = true;
        const formData = new FormData();
        const data = new Question();
        data.id = this.form.id ?? undefined;
        data.promptType = "TEXT";
        data.prompt = this.form.prompt;
        data.openQuestion = this.form.openQuestion;
        this.mountItemTOS(data);
        const strData = JSON.stringify(data);
        const jsonApp = new Blob([strData], { type: "application/json" });
        formData.append("question", jsonApp);
        if (this.questionId) {
          this.update(formData);
        } else {
          this.create(formData);
        }
      } else {
        if (this.v$.form.items.$each.$response.$errors[0].correct) {
          this.selectCorrect = true;
        }
      }
    },
    create(data: FormData): void {
      this.rest.createQuestion(this.quizId, data).subscribe({
        next: () => {
          this.$router.go(-1);
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    update(data: FormData): void {
      this.rest.updateQuestion(this.quizId, this.questionId, data).subscribe({
        next: () => {
          this.$router.go(-1);
        },
        error: () => {
          this.formLoader = false;
        },
      });
    },
    getQuestionById(): void {
      if (this.questionId) {
        this.service.question.subscribe({
          next: (response: any) => {
            if (response.id !== "") {
              this.form = response;
            }
          },
          error: () => {
            this.$router.go(-1);
          },
        });
        this.service.getQuestionById(this.quizId, this.questionId);
      }
    },
    getChapter(): Chapter {
      return JSON.parse(getItemLocalStorage("chapter") as string);
    },
    updateContents(event: any): void {
      if (this.questionId) {
        const data = new MaterialReferences();
        data.materialId = event.value.id;
        data.referenceTime = event.value.referenceTime;
        this.form.materialReferences?.push({ ...data });
      }
    },
    chooseCorrectItem(item: ItemQuestion, items: any): void {
      items = items.map((e) => {
        this.form.correctItem = null;
        e.correct = false;
        return e;
      });
      item.correct = true;
      this.selectCorrect = false;
    },
  },
});
</script>
